// Categories tab of the product editor: add from the catalog's taxonomy (every
// category, as Admin → Taxonomy has it, each with the path above it), drag to
// reorder — the first one drives the breadcrumb. Each category can give the
// product a weight: its place in that category's own order (lower first, 0 the
// first place), which the home page rows of the category list by; without one
// the product comes after the weighted ones.
import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { showNotification } from "aldoo-ra/Notification"
import { Select, NumberInput } from "../../../AdminShared/ui"
import { SortableList, DeleteIconButton } from "../shared/shared"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

export default function CategoriesTab({ editor, shouldShowField, isProductAdmin, isProductEditor, categories, categoryTree = [], categoryNames = {} }) {
  const { read, set } = editor
  const t = useProductEditorTexts()
  const [handingBack, setHandingBack] = useState(false)
  if (!shouldShowField("category") || !isProductAdmin || !categories) return null
  // an imported product: the import files it until an admin changes its set here (categoriesByHand), then leaves it
  const imported = !!read("source")?.importer
  const byHand = !!read("categoriesByHand")
  const handBack = async () => {
    setHandingBack(true)
    try {
      await editor.saveProduct({ categoriesFromImport: true })
      await editor.reload()
      showNotification({ message: t`Handed back: the next import run (or Apply rules) files it again.`, className: "bg-admin_success text-white" })
    } catch (error) {
      showNotification({ message: error.message, className: "bg-admin_error text-white" })
    } finally {
      setHandingBack(false)
    }
  }
  const current = read("category") || []
  const tree = categoryTree.length ? categoryTree : categories
  const byId = Object.fromEntries(tree.map((c) => [c._id, c]))
  // a product may be in a category above the assignable ones (an importer put
  // it there): it is named from the full list, the id only when nothing names it
  const nameOf = (id) => byId[id]?.name || categoryNames[id] || id
  const setCategories = (list) => set("category", list, true)
  const weights = read("categoryWeights") || {}
  const setWeight = (id, weight) => {
    const next = { ...weights }
    if (weight === null || weight === undefined) delete next[id]
    else next[id] = weight
    set("categoryWeights", next, true)
  }

  return (
    <div className="flex flex-col gap-3">
      <Typography component="h4" variant="admin_h5" className="my-0 inline-flex items-center gap-1.5">{t`Product Categories`}<HelpHint helpKey="vendor.product.setup#categories" /></Typography>
      <p className="text-sm text-admin_text_secondary my-0">{t`Add categories and reorder them. The first category is used for the breadcrumb navigation on the product page.`}</p>
      {imported && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-admin_text_secondary" data-testid="categories-import-state">
          <span>{byHand ? t`Set by hand: the import leaves these categories as they are.` : t`Filed by the import. A change here is kept: the import no longer files this product.`}</span>
          {byHand && isProductEditor && (
            <Button variant="admin_secondary" className="h-[30px] text-xs px-3" onClick={handBack} disabled={handingBack} data-testid="categories-back-to-import">
              {t`Back to the import's categories`}
            </Button>
          )}
        </div>
      )}
      <Select
        value=""
        onChange={(v) => v && !current.includes(v) && setCategories([...current, v])}
        placeholder={t`Add Category`}
        // what the search reads: the name, the path above it (a search for a
        // parent finds what is under it) and the id
        options={tree
          .filter((c) => !current.includes(c._id))
          .map((c) => ({ value: c._id, label: `${[...(c.path || []), c.name].join(" › ")} ${c._id}`, title: c.name, path: (c.path || []).join(" › ") }))}
        onItemRender={(option) => (
          <span className="flex flex-col min-w-0">
            <span className="text-sm text-admin_text truncate">{option.title}</span>
            <span className="text-[11px] text-admin_text_secondary truncate">
              {option.path && `${option.path} · `}
              <span className="font-mono">{option.value}</span>
            </span>
          </span>
        )}
        disabled={!isProductEditor}
        searchEnabled
        className="md:w-1/2"
        data-testid="pick-category"
      />
      {current.length > 0 && (
        <div className="mt-2">
          <p className="text-sm font-semibold mt-0 mb-1">{t`Selected Categories (drag to reorder):`}</p>
          <SortableList
            items={current}
            keyOf={(id) => id}
            disabled={!isProductEditor}
            onMove={(from, to) => {
              const items = [...current]
              const [moved] = items.splice(from, 1)
              items.splice(to, 0, moved)
              setCategories(items)
            }}
            itemClassName="mb-1"
            render={(id, index, handle) => (
              <div className={`flex items-center gap-3 p-3 rounded-md border ${index === 0 ? "border-2 border-admin_primary bg-admin_primary/5" : "border-admin_bordered bg-admin_paper"}`} data-testid="category-row">
                {handle}
                <span className="flex-1 min-w-0 flex flex-col">
                  <span className="text-sm">
                    {nameOf(id)}
                    {index === 0 && <span className="ml-2 text-xs font-semibold text-admin_primary">{t`(Primary - used in breadcrumb)`}</span>}
                  </span>
                  {/* the id, for whoever maps categories (an importer's, a search) */}
                  {nameOf(id) !== id && <span className="text-[11px] font-mono text-admin_text_secondary truncate" data-testid="category-id">{id}</span>}
                </span>
                <label className="flex items-center gap-2 shrink-0 text-xs text-admin_text_secondary" title={t`The product's place in this category's order: lower first (0 is the first place); empty comes after the positioned ones`}>
                  {t`Position`}
                  <HelpHint helpKey="vendor.product.setup#category-position" />
                  <NumberInput
                    value={typeof weights[id] === "number" ? weights[id] : null}
                    onChange={(v) => setWeight(id, v)}
                    allowEmpty
                    placeholder="—"
                    disabled={!isProductEditor}
                    className="w-20 h-9 py-1 text-sm"
                    data-testid="category-weight"
                  />
                </label>
                <DeleteIconButton onClick={() => setCategories(current.filter((x) => x !== id))} disabled={!isProductEditor} />
              </div>
            )}
          />
        </div>
      )}
    </div>
  )
}
